import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  InternetSearchApprovalRequest,
  AnsweredBy,
  AnswerStep,
  BotResponse,
  ClarificationRequest,
  Flag,
  ImageTag,
  InternetSearchDecision,
  KnowledgeSource,
  MessageThread,
  Reference,
  ReferredTo,
  StreamBlock,
  UploadedAsset,
} from '../../types/chatTypes'
import { ParsedCommand } from '../../types/commandTypes'
import { loadAssetImage } from '../../core/assetImageCache'
import { useAgents } from '../agents/useAgents'
import { useChatStream } from './useChatStream'

// Helper to create a default BotResponse
const createInitialBotResponse = (
  tempId: string,
  knowledgeSource: KnowledgeSource,
  isPrimary = false,
): BotResponse => ({
  message_id: tempId,
  knowledgeSource,
  answer: '',
  isStreaming: true,
  needsFeedback: true,
  isPrimary,
  isMinimized: !isPrimary, // Primary response is expanded, others are minimized
})

interface UseChatLogicProps {
  existingChatId?: string // For existing chats only
  initialMessageThreads?: MessageThread[]
  onChatEstablished?: (chatId: string) => void
  onMessageSentSuccessfully?: (messageId: string, chatId: string) => void
  isNewChatSession?: boolean
  agentId?: string
  /** Catalog model name the user picked, if any. */
  model?: string
}

export const useChatLogic = ({
  existingChatId,
  initialMessageThreads = [],
  onChatEstablished,
  onMessageSentSuccessfully,
  isNewChatSession = false,
  agentId,
  model,
}: UseChatLogicProps) => {
  const { currentAgent } = useAgents()

  // Chat ID management - use existing chat ID or generate new one
  const [currentChatId, setCurrentChatId] = useState(() => existingChatId || uuidv4())

  const [conversation, setConversation] = useState<MessageThread[]>(() => {
    return initialMessageThreads.length > 0 ? initialMessageThreads : []
  })
  const [isTyping, setIsTyping] = useState<boolean>(false)
  const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false) // Tracks if a message send operation is in progress
  const [currentPhase, setCurrentPhase] = useState<{
    tempId: string
    phase: 'internal_streaming' | 'llm_resumed'
  } | null>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const isFirstMessageSentRef = useRef(false)
  // The popup being resumed. A ref, not state: state lags a fast double click.
  const resumingInterruptIdRef = useRef<string | null>(null)

  // Function to check if conversation contains any documents/attachments
  const hasDocumentsInConversation = useMemo(() => {
    return conversation.some(thread => thread.attachedAssets?.length)
  }, [conversation])

  // Update current chat ID when existing chat ID changes (for navigation between existing chats)
  useEffect(() => {
    if (existingChatId && existingChatId !== currentChatId) {
      setCurrentChatId(existingChatId)
    }
  }, [existingChatId, currentChatId])

  const regenerateChatId = useCallback(() => {
    const newId = uuidv4()
    setCurrentChatId(newId)
    return newId
  }, [])

  // Clear conversation function for explicit clearing
  const clearConversation = useCallback(() => {
    setConversation([])
  }, [])

  const clearConversationAndRegenerateChatId = useCallback(() => {
    setConversation([])
    return regenerateChatId()
  }, [regenerateChatId])

  useEffect(() => {
    if (conversation.length === 0 && initialMessageThreads.length > 0) {
      setConversation(initialMessageThreads)
    }

    isFirstMessageSentRef.current = !isNewChatSession && conversation.length > 0
  }, [initialMessageThreads, isNewChatSession, conversation.length])

  const handleStreamingUpdate = useCallback(
    (
      partialMessage: string,
      tempId: string,
      messageId?: string,
      error?: Error & { httpStatus?: number },
      knowledgeSource?: KnowledgeSource,
      errorDetails?: { policy_violations?: string[] },
      responseSource?: 'internal_knowledge' | 'agent_llm',
      isSearchingKnowledge?: boolean,
    ) => {
      if (
        error?.message === 'USER_INPUT_CONTENT_VIOLATION' ||
        error?.message === 'LLM_PROVIDER_USER_INPUT_BLOCKED'
      ) {
        setConversation(prevConversation => {
          const updated = prevConversation.map(thread => {
            if (thread.tempId === tempId) {
              return {
                ...thread,
                inputValidationError: error,
                inputValidationErrorDetails: errorDetails,
                // Clear any answers since the input was invalid
                answers: [],
              }
            }
            return thread
          })
          return updated
        })
        setIsSendingMessage(false)
        return
      }

      // Handle normal streaming updates and other errors
      setConversation(prevConversation =>
        prevConversation.map(thread => {
          if (thread.tempId !== tempId) return thread

          // A resume request the server failed on (5xx) puts the popup back, so
          // the user can try again. Other failures leave it answered and the
          // input open: the next question closes the pause.
          const approval = thread.internetSearchApproval
          const httpStatus = error?.httpStatus
          if (
            httpStatus !== undefined &&
            httpStatus >= 500 &&
            approval &&
            approval.status !== 'pending' &&
            !thread.answers.some(a => a.answer)
          ) {
            return { ...thread, answers: [], internetSearchApproval: { ...approval, status: 'pending' } }
          }

          const updatedAnswers = [...thread.answers]
          let answerIndex = -1

          if (responseSource) {
            // Find existing answer by response source
            if (responseSource === 'internal_knowledge') {
              answerIndex = updatedAnswers.findIndex(a => a.knowledgeSource === 'INTERNAL_KB')
            } else if (responseSource === 'agent_llm') {
              answerIndex = updatedAnswers.findIndex(a => a.knowledgeSource !== 'INTERNAL_KB')
            }

            // If no answer exists for this source, create one dynamically
            if (answerIndex === -1) {
              const newKnowledgeSource: KnowledgeSource =
                responseSource === 'internal_knowledge' ? 'INTERNAL_KB' : knowledgeSource || 'LLM_ONLY'
              const newAnswer = createInitialBotResponse(
                `${tempId}-${responseSource}`,
                newKnowledgeSource,
                responseSource === 'internal_knowledge', // Internal knowledge is primary when it appears
              )

              // Add new answer - internal knowledge goes at end, agent at beginning
              if (responseSource === 'internal_knowledge') {
                updatedAnswers.push(newAnswer) // Add internal at end
                answerIndex = updatedAnswers.length - 1

                // When internal knowledge appears, minimize the existing GPT response
                if (updatedAnswers.length === 2) {
                  updatedAnswers[0] = { ...updatedAnswers[0], isMinimized: true, isPrimary: false }
                }
              } else {
                updatedAnswers.unshift(newAnswer) // Add agent at beginning
                answerIndex = 0
              }
            }
          } else {
            // Fallback for responses without source - update the first/only answer
            answerIndex = 0
          }

          if (answerIndex === -1 || answerIndex >= updatedAnswers.length) return thread

          const targetAnswer = updatedAnswers[answerIndex]

          // Update the answer with new content
          updatedAnswers[answerIndex] = {
            ...targetAnswer,
            message_id: messageId || targetAnswer.message_id,
            answer: error ? targetAnswer.answer : partialMessage,
            isStreaming: !error,
            error: error?.message,
            errorDetails: errorDetails,
            knowledgeSource: knowledgeSource || targetAnswer.knowledgeSource,
            isSearchingKnowledge: error
              ? false
              : isSearchingKnowledge ?? targetAnswer.isSearchingKnowledge,
          }

          return { ...thread, answers: updatedAnswers }
        }),
      )
      if (error) {
        resumingInterruptIdRef.current = null
        setIsSendingMessage(false) // Stream ended due to error
      }
    },
    [],
  )

  const handleResponseTransition = useCallback(
    (
      tempId: string,
      responseSource: 'internal_knowledge' | 'agent_llm',
      phase: 'internal_streaming' | 'llm_resumed',
    ) => {
      setCurrentPhase({ tempId, phase })

      setConversation(prevConversation =>
        prevConversation.map(thread => {
          if (thread.tempId !== tempId) return thread

          const updatedAnswers = [...thread.answers]

          if (phase === 'internal_streaming') {
            // Internal knowledge found - minimize existing LLM response and add internal response
            const llmAnswerIndex = updatedAnswers.findIndex(a => a.knowledgeSource !== 'INTERNAL_KB')

            if (llmAnswerIndex !== -1) {
              // Minimize the existing LLM response but keep it streaming
              updatedAnswers[llmAnswerIndex] = {
                ...updatedAnswers[llmAnswerIndex],
                isMinimized: true,
                isPrimary: false,
                isStreaming: true, // Keep streaming for when LLM resumes
              }
            }

            // Add internal knowledge response if it doesn't exist
            const internalIndex = updatedAnswers.findIndex(a => a.knowledgeSource === 'INTERNAL_KB')
            if (internalIndex === -1) {
              const internalAnswer = createInitialBotResponse(`${tempId}-internal_knowledge`, 'INTERNAL_KB', true)
              updatedAnswers.push(internalAnswer)
            }
          } else if (phase === 'llm_resumed') {
            // LLM streaming resumed - ensure LLM answer is ready to continue streaming
            const llmAnswerIndex = updatedAnswers.findIndex(a => a.knowledgeSource !== 'INTERNAL_KB')

            if (llmAnswerIndex !== -1) {
              // Ensure LLM answer is still streaming and minimized
              updatedAnswers[llmAnswerIndex] = {
                ...updatedAnswers[llmAnswerIndex],
                isStreaming: true,
                isMinimized: true,
                isPrimary: false,
              }
            }

            // Internal answer remains primary but no longer streaming
            const internalIndex = updatedAnswers.findIndex(a => a.knowledgeSource === 'INTERNAL_KB')
            if (internalIndex !== -1) {
              updatedAnswers[internalIndex] = {
                ...updatedAnswers[internalIndex],
                isStreaming: false, // Internal is done when LLM resumes
                isPrimary: true,
                isMinimized: false,
              }
            }
          }

          return { ...thread, answers: updatedAnswers }
        }),
      )
    },
    [],
  )

  // Tells the page the new chat exists on the server (URL, sidebar), once.
  const establishChat = useCallback(() => {
    if (isNewChatSession && !isFirstMessageSentRef.current && onChatEstablished) {
      onChatEstablished(currentChatId)
      isFirstMessageSentRef.current = true
    }
  }, [isNewChatSession, onChatEstablished, currentChatId])

  const handleMessageComplete = useCallback(
    (
      completedMessage: string,
      tempId: string,
      messageId: string,
      knowledgeSource?: KnowledgeSource,
      references?: Reference[],
    ) => {
      setConversation(prevConversation => {
        return prevConversation.map(thread => {
          if (thread.tempId !== tempId) return thread

          // Find the answer that matches this completion
          let answerIndex = -1

          // First try to find by exact message_id match
          answerIndex = thread.answers.findIndex(a => a.message_id === messageId)

          // If not found by exact ID, fall back to knowledge source matching
          if (answerIndex === -1) {
            if (thread.answers.length > 1) {
              // For dual responses, find by knowledge source
              if (knowledgeSource === 'INTERNAL_KB') {
                answerIndex = thread.answers.findIndex(a => a.knowledgeSource === 'INTERNAL_KB')
              } else {
                // Find the non-internal knowledge answer (LLM response)
                answerIndex = thread.answers.findIndex(a => a.knowledgeSource !== 'INTERNAL_KB')
              }
            } else {
              // For single responses
              answerIndex = 0
            }
          }

          if (answerIndex === -1) return thread

          const updatedAnswers = [...thread.answers]

          // Update the answer with completion data
          updatedAnswers[answerIndex] = {
            ...updatedAnswers[answerIndex],
            message_id: messageId,
            answer: completedMessage,
            isStreaming: false,
            error: undefined,
            knowledgeSource: knowledgeSource || updatedAnswers[answerIndex].knowledgeSource,
            references: references && references.length > 0 ? references : updatedAnswers[answerIndex].references,
          }

          return { ...thread, answers: updatedAnswers }
        })
      })

      // Only set sending to false when all answers are done streaming
      const allAnswersComplete = conversation.every(thread => thread.answers.every(answer => !answer.isStreaming))

      if (allAnswersComplete) {
        setIsSendingMessage(false)
      }

      if (onMessageSentSuccessfully) {
        onMessageSentSuccessfully(messageId, currentChatId)
      }

      establishChat()
    },
    [onMessageSentSuccessfully, currentChatId, establishChat, conversation],
  )

  // Sources arrive before the answer tokens; attach them to the streaming
  // answer(s) of this thread so inline citation markers resolve live.
  const handleReferencesReceived = useCallback((references: Reference[], tempId: string) => {
    if (!references.length) return
    setConversation(prevConversation =>
      prevConversation.map(thread => {
        if (thread.tempId !== tempId) return thread
        const updatedAnswers = thread.answers.map(answer =>
          answer.isStreaming ? { ...answer, references } : answer,
        )
        return { ...thread, answers: updatedAnswers }
      }),
    )
  }, [])

  // Image tags arrive before the answer tokens; attach them to the streaming
  // answer(s) of this thread so inline `[IMG#]` markers resolve to their asset
  // image live.
  const handleImagesReceived = useCallback((images: ImageTag[], tempId: string) => {
    if (!images.length) return
    // Warm the blob cache now, before the answer tokens arrive, so the image is
    // ready the moment its `[IMG#]` marker resolves into an <AssetImage>.
    images.forEach(image => {
      // Warm failures are non-fatal: AssetImage refetches on mount and renders
      // its own fallback. Without the catch a 404 here becomes an unhandled
      // promise rejection (dev-overlay "Uncaught runtime error").
      loadAssetImage(image.url).catch(() => undefined)
    })
    setConversation(prevConversation =>
      prevConversation.map(thread => {
        if (thread.tempId !== tempId) return thread
        const updatedAnswers = thread.answers.map(answer =>
          answer.isStreaming ? { ...answer, images } : answer,
        )
        return { ...thread, answers: updatedAnswers }
      }),
    )
  }, [])

  // A clarification arrives as its own frame after the intro text streamed
  // in as the answer, so the answer already exists: attach the questions so
  // the card renders and end the loading state so the user can answer.
  const handleClarification = useCallback((payload: ClarificationRequest, tempId: string) => {
    setConversation(prevConversation =>
      prevConversation.map(thread => {
        if (thread.tempId !== tempId) return thread
        const updatedAnswers = thread.answers.map((answer, index) =>
          index === 0
            ? { ...answer, message_id: payload.message_id, clarification: payload, isStreaming: false }
            : answer,
        )
        return { ...thread, answers: updatedAnswers }
      }),
    )
    setIsSendingMessage(false)
  }, [])

  // The popup replaces the pending answer; the chat input stays disabled until it is answered.
  // The popup keeps the turn's attention flags for the resumed answer.
  const handleInternetSearchApproval = useCallback(
    (payload: InternetSearchApprovalRequest, tempId: string) => {
      setConversation(prev =>
        prev.map(thread =>
          thread.tempId !== tempId
            ? thread
            : {
                ...thread,
                answers: [],
                internetSearchApproval: {
                  ...payload,
                  status: 'pending',
                  flags: thread.answers[thread.answers.length - 1]?.flags,
                },
              },
        ),
      )
      setIsSendingMessage(false)
      establishChat()
    },
    [establishChat],
  )

  // Graph agents publish their retrieval steps as they happen; the bubble keeps
  // them so each step renders where it occurred in the answer.
  const handleStreamBlocks = useCallback((blocks: StreamBlock[], tempId: string) => {
    setConversation(prevConversation =>
      prevConversation.map(thread => {
        if (thread.tempId !== tempId) return thread
        const updatedAnswers = thread.answers.map((answer, index) =>
          index === 0 ? { ...answer, blocks } : answer,
        )
        return { ...thread, answers: updatedAnswers }
      }),
    )
  }, [])

  const handleFlagsReceived = useCallback(
    (flags: Flag[]) => {
      setConversation(prevConversation => {
        if (prevConversation.length === 0) return prevConversation

        const lastThreadIndex = prevConversation.length - 1
        const lastThread = prevConversation[lastThreadIndex]

        if (lastThread.answers.length === 0) return prevConversation

        const updatedAnswers = [...lastThread.answers]
        updatedAnswers[updatedAnswers.length - 1] = {
          ...updatedAnswers[updatedAnswers.length - 1],
          flags,
        }

        const updatedConversation = [...prevConversation]
        updatedConversation[lastThreadIndex] = { ...lastThread, answers: updatedAnswers }

        return updatedConversation
      })
    },
    [],
  )

  // Tag the streaming answer so it asks the reader to check the source.
  // Same shape as handleFlagsReceived.
  const handleHallucinationWarning = useCallback(() => {
    setConversation(prevConversation => {
      if (prevConversation.length === 0) return prevConversation

      const lastThreadIndex = prevConversation.length - 1
      const lastThread = prevConversation[lastThreadIndex]
      if (lastThread.answers.length === 0) return prevConversation

      const updatedAnswers = [...lastThread.answers]
      updatedAnswers[updatedAnswers.length - 1] = {
        ...updatedAnswers[updatedAnswers.length - 1],
        verifySources: true,
      }

      const updatedConversation = [...prevConversation]
      updatedConversation[lastThreadIndex] = { ...lastThread, answers: updatedAnswers }

      return updatedConversation
    })
  }, [])

  // Tags the streaming answer with its author, which arrives before the tokens.
  const handleAnsweredBy = useCallback((answeredBy: AnsweredBy, tempId: string) => {
    setConversation(prevConversation =>
      prevConversation.map(thread => {
        if (thread.tempId !== tempId) return thread
        const updatedAnswers = thread.answers.map(answer =>
          answer.isStreaming ? { ...answer, answeredBy } : answer,
        )
        return { ...thread, answers: updatedAnswers }
      }),
    )
  }, [])

  // The assistant the streaming answer points the user to.
  const handleReferredTo = useCallback((referredTo: ReferredTo, tempId: string) => {
    setConversation(prevConversation =>
      prevConversation.map(thread => {
        if (thread.tempId !== tempId) return thread
        const updatedAnswers = thread.answers.map(answer =>
          answer.isStreaming ? { ...answer, referredTo } : answer,
        )
        return { ...thread, answers: updatedAnswers }
      }),
    )
  }, [])

  // Answer 0, as for a clarification: the final envelope already ended the streaming state.
  const handleWebSearchQueries = useCallback((webSearchQueries: string[], tempId: string) => {
    setConversation(prevConversation =>
      prevConversation.map(thread => {
        if (thread.tempId !== tempId) return thread
        const updatedAnswers = thread.answers.map((answer, index) =>
          index === 0 ? { ...answer, webSearchQueries } : answer,
        )
        return { ...thread, answers: updatedAnswers }
      }),
    )
  }, [])

  const handleStep = useCallback((step: AnswerStep | undefined, tempId: string) => {
    setConversation(prevConversation =>
      prevConversation.map(thread => {
        if (thread.tempId !== tempId) return thread
        const updatedAnswers = thread.answers.map((answer, index) => (index === 0 ? { ...answer, step } : answer))
        return { ...thread, answers: updatedAnswers }
      }),
    )
  }, [])

  // The internet badge as placeholder only when internet is the agent's only knowledge tool
  const isInternetSearchOnly = useMemo(() => {
    const tools = currentAgent?.tools ?? []
    const searchesKnowledge = tools.some(
      tool => tool === 'KNOWLEDGE_SEARCH' || tool === 'GRAPH_SEARCH' || tool.startsWith('retrieve_knowledge_'),
    )
    return tools.includes('INTERNET_SEARCH') && !searchesKnowledge
  }, [currentAgent?.tools])
  const isInternalSearchOnly = useMemo(
    () => (currentAgent?.slug || '').toLowerCase() === 'internal-data',
    [currentAgent?.slug],
  )

  // ----- Single Stream Configuration -----
  const streamConfig = {
    chatId: currentChatId,
    agentId: agentId,
    model,
  }

  // Single unified stream that handles both internal knowledge and agent responses
  const {
    isLoading,
    error: streamError,
    sendMessage: streamSendMessage,
    stopStream,
    dualResponse,
    currentResponseSource,
  } = useChatStream(
    streamConfig,
    handleMessageComplete,
    handleStreamingUpdate,
    handleResponseTransition,
    handleFlagsReceived,
    handleReferencesReceived,
    handleImagesReceived,
    handleClarification,
    handleStreamBlocks,
    handleHallucinationWarning,
    handleAnsweredBy,
    handleReferredTo,
    handleInternetSearchApproval,
    handleWebSearchQueries,
    handleStep,
  )

  const isStreamActive = isLoading

  // Abort the in-flight response: stop the stream and clear the sending state
  // so the composer returns to an idle, typeable state.
  const stopStreaming = useCallback(() => {
    stopStream()
    setIsSendingMessage(false)
  }, [stopStream])

  // While a popup waits, nothing else may be sent (same rule as `isInputDisabled`).
  const hasPendingInternetSearch = conversation.some(t => t.internetSearchApproval?.status === 'pending')

  const sendMessage = useCallback(
    (message: string, attachedAssets: UploadedAsset[] = [], commands?: ParsedCommand[]) => {
      if (
        !message.trim() ||
        isSendingMessage ||
        isStreamActive ||
        hasPendingInternetSearch ||
        currentAgent?.id !== agentId
      )
        return // Prevent sending if already processing or a popup waits

      setIsTyping(false)
      setIsSendingMessage(true) // Indicate that a send operation has started

      const tempId = `temp-${uuidv4()}`

      // Start with a single placeholder response - we'll add more dynamically based on backend streaming
      // Use the user-selected knowledge source or default based on agent type
      const knowledgeSource: KnowledgeSource = isInternetSearchOnly
        ? 'INTERNET_SEARCH'
        : isInternalSearchOnly
        ? 'INTERNAL_KB'
        : 'LLM_ONLY'
      // Attached files are processed (OCR, image descriptions) before the agent starts; the
      // stream only keeps the connection alive meanwhile, so the step comes from here.
      const files = attachedAssets.map(asset => asset.filename)
      const initialAnswer: BotResponse = {
        ...createInitialBotResponse(tempId, knowledgeSource, true),
        step: files.length
          ? { kind: attachedAssets.every(a => a.content_type.startsWith('image/')) ? 'image' : 'files', items: files }
          : undefined,
      }

      const thread: MessageThread = {
        tempId,
        question: message,
        answers: [initialAnswer],
        attachedAssets,
        appliedCommands: commands,
      }

      setConversation(prev => [...prev, thread])

      // Send message through single stream - backend handles dual strategy.
      // Slash-commands (if any) ride along as a structured `commands` payload.
      const additionalPayload = commands && commands.length > 0 ? { commands } : undefined
      const assetIds = attachedAssets.map(asset => asset.id)
      streamSendMessage(message, tempId, currentAgent, assetIds, additionalPayload)
    },
    [
      streamSendMessage,
      currentAgent,
      isSendingMessage,
      isStreamActive,
      hasPendingInternetSearch,
      agentId,
      isInternetSearchOnly,
      isInternalSearchOnly,
    ],
  )

  // Drop the card from the originating thread (so it reads as plain text from
  // then on, matching what a reload shows) and send the combined Q:/A: answers
  // as a normal message. The agent's skip-guard sees the previous clarification
  // and retrieves straight away instead of asking again.
  const submitClarification = useCallback(
    (tempId: string, qaString: string) => {
      if (hasPendingInternetSearch) return
      setConversation(prevConversation =>
        prevConversation.map(thread => {
          if (thread.tempId !== tempId) return thread
          return { ...thread, answers: thread.answers.map(answer => ({ ...answer, clarification: undefined })) }
        }),
      )
      sendMessage(qaString)
    },
    [hasPendingInternetSearch, sendMessage],
  )

  // The user's answer to the popup: continue the paused turn through the
  // resume endpoint, on the same thread, with the normal stream listeners.
  const resolveInternetSearch = useCallback(
    (tempId: string, decision: InternetSearchDecision, queries?: (string | null)[]) => {
      const thread = conversation.find(t => t.tempId === tempId)
      const approval = thread?.internetSearchApproval
      if (
        !thread ||
        !approval ||
        approval.status !== 'pending' ||
        resumingInterruptIdRef.current === approval.interrupt_id ||
        isSendingMessage ||
        isStreamActive
      )
        return
      resumingInterruptIdRef.current = approval.interrupt_id
      setIsSendingMessage(true)
      setConversation(prev =>
        prev.map(t =>
          t.tempId !== tempId
            ? t
            : {
                ...t,
                answers: [
                  {
                    ...createInitialBotResponse(tempId, decision === 'send' ? 'INTERNET_SEARCH' : 'LLM_ONLY', true),
                    flags: approval.flags,
                  },
                ],
                internetSearchApproval: { ...approval, status: decision === 'send' ? 'sent' : 'internal' },
              },
        ),
      )
      streamSendMessage(thread.question ?? '', tempId, currentAgent, undefined, {
        resume: { interruptId: approval.interrupt_id, decision, queries: decision === 'send' ? queries : undefined },
      })
    },
    [conversation, currentAgent, isSendingMessage, isStreamActive, streamSendMessage],
  )

  const handleRetry = useCallback(
    (question: string, tempIdToRetry: string, searchKnowledgeSource?: KnowledgeSource) => {
      if (hasPendingInternetSearch) return
      // The retry runs under the same directives (`@` pick, `/org`) as the original.
      const commands = conversation.find(thread => thread.tempId === tempIdToRetry)?.appliedCommands
      setConversation(prev => prev.filter(thread => thread.tempId !== tempIdToRetry))
      // Use a timeout to ensure state update is processed before resending
      setTimeout(() => {
        sendMessage(question, undefined, commands)
      }, 0)
    },
    [conversation, hasPendingInternetSearch, sendMessage],
  )

  const toggleResponseMinimized = useCallback((threadTempId: string, responseMessageId: string) => {
    setConversation(prevConversation =>
      prevConversation.map(thread => {
        if (thread.tempId !== threadTempId) return thread

        const updatedAnswers = thread.answers.map(answer => {
          if (answer.message_id === responseMessageId) {
            return { ...answer, isMinimized: !answer.isMinimized }
          }
          return answer
        })

        return { ...thread, answers: updatedAnswers }
      }),
    )
  }, [])

  // Combined loading state for UI
  const combinedIsLoading = isSendingMessage || isStreamActive
  const isInputDisabled = combinedIsLoading || hasPendingInternetSearch

  return {
    conversation,
    setConversation,
    clearConversation,
    clearConversationAndRegenerateChatId,
    regenerateChatId,
    isTyping,
    setIsTyping,
    isLoading: combinedIsLoading, // Unified loading state
    error: streamError, // Expose error from the stream
    sendMessage,
    stopStreaming, // Abort the in-flight response (closes the stream client-side)
    handleRetry,
    submitClarification, // Answer a pending clarification card
    resolveInternetSearch, // Answer the internet search popup: send the (edited) query, or go without
    toggleResponseMinimized, // Function to expand/minimize responses
    chatContainerRef,
    isInputDisabled,
    currentChatId,
    hasDocumentsInConversation, // Expose this for use in QuestionInput
    dualResponse, // Expose dual response for components that need it
    currentResponseSource, // Expose current response source for real-time indicators
    currentPhase, // Expose current transition phase for UI indicators
  }
}
